import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateTooltipDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { TooltipEditorForm } from './TooltipEditorForm';

interface TooltipModalEditorProps {
  id?: string;
  tooltipKey?: string;
  description?: string;
  tooltip: CreateUpdateTooltipDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    tooltip: CreateUpdateTooltipDto,
    id: string,
  ) => Promise<{ saved: boolean; errors: string[] }>;
}

export const TooltipModalEditor: React.FC<TooltipModalEditorProps> = props => {
  const [tooltip, setTooltip] = useState(props.tooltip);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setTooltip(props.tooltip);
    }
  }, [props.tooltip, props.show]);

  const handleSave = () => {
    if (!props.id) {
      return;
    }
    setIsLoading(true);
    props
      .onCompleted(tooltip, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateTooltipDto>) => {
    setTooltip({ ...tooltip, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t(tk.tooltips.tooltipEdit)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TooltipEditorForm
          tooltipKey={props.tooltipKey}
          description={props.description}
          tooltip={tooltip}
          propertyChange={propertyChange}
        />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo id={props.id} method={pok.tooltips.getLastEditor} />
        )}
        <Button
          variant="outline-secondary"
          onClick={props.handleClose}
          disabled={isLoading}
        >
          {t(tk.button.close)}
        </Button>
        <Button variant="primary" onClick={handleSave} isLoading={isLoading}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
